// API client for Stacks backend

// Auto-detect backend URL based on current hostname
function getBackendUrl() {
    // Use explicit environment variable if set
    if (process.env.REACT_APP_BACKEND_URL) {
        return process.env.REACT_APP_BACKEND_URL;
    }
    
    // Auto-detect based on current hostname
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    // Production: app.getstacks.xyz -> try api.getstacks.xyz first, then same domain
    if (hostname === 'app.getstacks.xyz' || hostname.includes('getstacks.xyz')) {
        // Try api subdomain first
        if (hostname.startsWith('app.')) {
            return `${protocol}//api.${hostname.substring(4)}`;
        }
        // If already on getstacks.xyz, use same domain
        return `${protocol}//${hostname}`;
    }
    
    // Development: localhost (backend runs on port 8000)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8000';
    }
    
    // Fallback: try to construct from current origin
    // If frontend is on a subdomain, try api subdomain
    if (hostname.includes('.')) {
        const parts = hostname.split('.');
        if (parts[0] === 'app' && parts.length > 1) {
            // app.domain.com -> api.domain.com
            parts[0] = 'api';
            return `${protocol}//${parts.join('.')}`;
        }
    }
    
    // Last resort: use same origin (backend might be on same domain with /api path)
    return window.location.origin;
}

const BASE_URL = getBackendUrl();
const API_URL = `${BASE_URL.replace(/\/+$/, '')}/api`;

// Log the detected backend URL for debugging
console.log('[API] Backend URL:', BASE_URL);
console.log('[API] API URL:', API_URL);

class ApiClient {
    constructor() {
        this.token = localStorage.getItem('stacks_token');
    }

    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('stacks_token', token);
        } else {
            localStorage.removeItem('stacks_token');
        }
    }

    getToken() {
        return this.token || localStorage.getItem('stacks_token');
    }

    async request(endpoint, options = {}) {
        const url = `${API_URL}${endpoint}`;
        const token = this.getToken();

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        let response;
        try {
            response = await fetch(url, {
                ...options,
                headers,
            });
        } catch (err) {
            // Network error or request failed
            console.error('API request failed:', { url, error: err.message });
            throw new Error(`Network error: Unable to connect to ${BASE_URL}. Please check your connection and ensure the backend is running.`);
        }

        if (response.status === 204) {
            return null;
        }

        // Parse response
        let data;
        try {
            const text = await response.text();
            data = text ? JSON.parse(text) : {};
        } catch (err) {
            // Response is not valid JSON (HTML error page, etc.)
            if (!response.ok) {
                // Provide specific error based on status code
                const statusMessages = {
                    400: 'Bad request. Please check your input and try again.',
                    401: 'Authentication failed. Please check your credentials.',
                    403: 'Access forbidden. Please verify your email before logging in.',
                    404: 'Requested resource not found.',
                    409: 'Conflict. This resource already exists.',
                    422: 'Invalid input. Please check your form data.',
                    429: 'Too many requests. Please try again later.',
                    500: 'Internal server error. Please try again later.',
                    502: 'Service temporarily unavailable. Please try again.',
                    503: 'Service unavailable. Please try again later.',
                };
                const errorMsg = statusMessages[response.status] || `Server error (${response.status}). Please try again.`;
                throw new Error(errorMsg);
            }
            throw new Error('Invalid response from server');
        }

        if (!response.ok) {
            // Handle validation errors (422) - these are from Pydantic
            if (response.status === 422 && Array.isArray(data.detail)) {
                const firstError = data.detail[0];
                const field = firstError.loc?.[firstError.loc.length - 1] || '';
                const errorMsg = firstError.msg || 'Invalid input';
                throw new Error(field ? `${field}: ${errorMsg}` : errorMsg);
            }
            
            // Handle other errors - extract detail string
            const errorMessage = typeof data.detail === 'string' 
                ? data.detail 
                : (data.message || 'An unexpected error occurred. Please try again.');
            const error = new Error(errorMessage);
            error.status = response.status;
            throw error;
        }

        return data;
    }

    // Auth
    async register(email, password, name) {
        const data = await this.request('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ email, password, name }),
        });
        // Registration now returns message, not token
        return data;
    }

    async login(email, password) {
        const data = await this.request('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        this.setToken(data.token);
        return data;
    }

    async verifyEmail(token) {
        const data = await this.request('/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
        this.setToken(data.token);
        return data;
    }

    async getMe() {
        return this.request('/auth/me');
    }

    async forgotPassword(email) {
        return this.request('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async resetPassword(token, password) {
        return this.request('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, password }),
        });
    }

    logout() {
        this.setToken(null);
    }

    // Stacks
    async getStacks(includeArchived = false) {
        return this.request(`/stacks?include_archived=${includeArchived}`);
    }

    async getStack(id) {
        return this.request(`/stacks/${id}`);
    }

    async createStack(data) {
        return this.request('/stacks', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateStack(id, data) {
        return this.request(`/stacks/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async archiveStack(id) {
        return this.request(`/stacks/${id}/archive`, {
            method: 'POST',
        });
    }

    async restoreStack(id) {
        return this.request(`/stacks/${id}/restore`, {
            method: 'POST',
        });
    }

    async deleteStack(id) {
        return this.request(`/stacks/${id}`, {
            method: 'DELETE',
        });
    }

    // Completions
    async getCompletions(stackId, startDate, endDate) {
        let url = '/completions?';
        if (stackId) url += `stack_id=${stackId}&`;
        if (startDate) url += `start_date=${startDate}&`;
        if (endDate) url += `end_date=${endDate}&`;
        return this.request(url);
    }

    async createCompletion(stackId, itemId, completedAt) {
        return this.request('/completions', {
            method: 'POST',
            body: JSON.stringify({
                stack_id: stackId,
                item_id: itemId,
                completed_at: completedAt,
            }),
        });
    }

    async createCompletionsBatch(completions) {
        return this.request('/completions/batch', {
            method: 'POST',
            body: JSON.stringify(completions),
        });
    }

    async deleteCompletion(id) {
        return this.request(`/completions/${id}`, {
            method: 'DELETE',
        });
    }

    // Sync
    async sync(lastSynced) {
        return this.request('/sync', {
            method: 'POST',
            body: JSON.stringify({ last_synced: lastSynced }),
        });
    }
}

export const api = new ApiClient();
